import styled from '@emotion/styled';
import { Button, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';

const PrevButtonContainer = styled(Button)<{ isFirstSlideActive?: boolean; disabled: boolean }>`
  margin-right: ${tokens.spacer.x1};
  padding: 0 ${tokens.spacer.x1};
  background: none;
  border: none;
  cursor: ${({ isFirstSlideActive }) => (isFirstSlideActive ? 'initial' : 'pointer')};

  &:hover {
    background: ${({ disabled }) => (disabled ? 'none' : 'revert')};
  }
`;

const NextButtonContainer = styled(PrevButtonContainer)<{
  isLastSlideActive?: boolean;
  disabled: boolean;
}>`
  margin-right: 0;
  margin-left: ${tokens.spacer.x1};
  cursor: ${({ isLastSlideActive }) => (isLastSlideActive ? 'initial' : 'pointer')};

  &:hover {
    background: ${({ disabled }) => (disabled ? 'none' : 'revert')};
  }

  & svg {
    transform: rotate(-180deg);
  }
`;

type ArrowButtonProps = {
  isFirstSlideActive: boolean;
  onClick: () => void;
};

export const PrevButton = ({ isFirstSlideActive, onClick }: ArrowButtonProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <PrevButtonContainer
      kind="ghost"
      size="s"
      isFirstSlideActive={isFirstSlideActive}
      disabled={isFirstSlideActive}
      onClick={onClick}
      title={t('activation:space-overview.for-you.featured-insights.slider.previous-insight')}
      data-testid="featured-insights-arrow-button-previous"
    >
      {
        (
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.97549 1.40518C5.19865 1.18202 5.19865 0.820212 4.97549 0.597055C4.75233 0.373898 4.39052 0.373898 4.16737 0.597055L0.167368 4.59705C0.112581 4.65184 0.0712446 4.71499 0.043359 4.78238C0.0154202 4.84976 0 4.92364 0 5.00112C0 5.0786 0.0154202 5.15247 0.043359 5.21985C0.0712446 5.28725 0.112581 5.35039 0.167368 5.40518L4.16737 9.40518C4.39052 9.62833 4.75233 9.62833 4.97549 9.40518C5.19865 9.18202 5.19865 8.82021 4.97549 8.59706L1.95098 5.57254H15.4286C15.7442 5.57254 16 5.31671 16 5.00112C16 4.68552 15.7442 4.42969 15.4286 4.42969H1.95098L4.97549 1.40518Z"
              fill={isFirstSlideActive ? tokens.color.text_disabled : tokens.color.text_default}
              fillOpacity="0.89"
            />
          </svg>
        ) as string & React.ReactNode
      }
    </PrevButtonContainer>
  );
};

type NextButtonProps = {
  isLastSlideActive: boolean;
  onClick: () => void;
};

export const NextButton = ({ isLastSlideActive, onClick }: NextButtonProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <NextButtonContainer
      kind="ghost"
      size="s"
      isLastSlideActive={isLastSlideActive}
      disabled={isLastSlideActive}
      onClick={onClick}
      title={t('activation:space-overview.for-you.featured-insights.slider.next-insight')}
      data-testid="featured-insights-arrow-button-next"
    >
      {
        (
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.97549 1.40518C5.19865 1.18202 5.19865 0.820212 4.97549 0.597055C4.75233 0.373898 4.39052 0.373898 4.16737 0.597055L0.167368 4.59705C0.112581 4.65184 0.0712446 4.71499 0.043359 4.78238C0.0154202 4.84976 0 4.92364 0 5.00112C0 5.0786 0.0154202 5.15247 0.043359 5.21985C0.0712446 5.28725 0.112581 5.35039 0.167368 5.40518L4.16737 9.40518C4.39052 9.62833 4.75233 9.62833 4.97549 9.40518C5.19865 9.18202 5.19865 8.82021 4.97549 8.59706L1.95098 5.57254H15.4286C15.7442 5.57254 16 5.31671 16 5.00112C16 4.68552 15.7442 4.42969 15.4286 4.42969H1.95098L4.97549 1.40518Z"
              fill={isLastSlideActive ? tokens.color.text_disabled : tokens.color.text_default}
              fillOpacity="0.89"
            />
          </svg>
        ) as string & React.ReactNode
      }
    </NextButtonContainer>
  );
};
